/**
 * The case page (docs/19 A3), top to bottom:
 *
 * 1. **The head** — file number, his name, the stage, and every deadline
 *    running, each saying whether it is statutory or set by the office.
 *    **Needs a look** sits under it, only when something needs the officer
 *    (Phase A, A10) — the server's list, each line saying what to do.
 * 2. **The next action** — one card, always: something to do, waiting, or
 *    needing something first, with the upload slot or the choice right there.
 * 3. **Something has come in** — always there, for papers out of order.
 * 4. **Note something not on paper** — one line, rare.
 * 5. **The case file** — his particulars (corrected here, each with its
 *    reason), the decisions, the history and the papers.
 * 6. **The documents** — done, next, waiting, skipped with the reason.
 *
 * Then what the office writes beyond the twenty, as before.
 *
 * **The card is worked out on the server from the case record every time the
 * page loads, so it is never stale** — and the queue reads the same card. This
 * page decides nothing about what is possible; the parts under `./case/`
 * render what the server says and post the commands it names.
 *
 * The CGM's view is the officer's minus the ability to act: the card shows
 * what the case is on and names the acts without offering them, and nothing
 * on the page records anything. The server refuses a CGM's write by the role
 * table's default-deny; this is the drawn half.
 */

import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import type { CaseDetail } from '../api/client'
import { ErrorEnvelope } from '../components/bits'
import { CaseChat } from '../components/chat'
import { useSession } from '../lib/session'
import { CaseFile } from './case/CaseFile'
import { CaseHeader } from './case/CaseHeader'
import { CourtDocuments } from './case/CourtDocuments'
import { DocumentsRail } from './case/DocumentsRail'
import { NeedsALook } from './case/NeedsALook'
import { NextActionCard } from './case/NextActionCard'
import { NoteLine } from './case/NoteLine'
import { SomethingCameIn } from './case/SomethingCameIn'

export function CasePage() {
  const { caseId } = useParams()
  const { user } = useSession()
  const cgm = user?.role === 'cgm'
  // Null is unknown, never "officer" (the field's own contract) — an account
  // whose role the client cannot see gets the reading surfaces and none of
  // the acting ones, same as the CGM, until the role arrives.
  const readOnly = cgm || user?.role == null
  const [asking, setAsking] = useState(false)
  const detail = useQuery({
    queryKey: ['case', caseId],
    queryFn: () => api.getCase(caseId as string),
    enabled: Boolean(caseId),
  })
  // Reference readings for a case whose officer never settled English — every
  // case confirmed before the English screen existed. Readings, labelled as
  // readings, never written anywhere; fetched only when something is missing.
  const needsReadings = Boolean(
    detail.data &&
    !((detail.data.employee as Record<string, string | null>)['name_en'] ?? '').trim(),
  )
  const readings = useQuery({
    queryKey: ['intake-english', caseId],
    queryFn: () => api.intakeEnglish(caseId as string),
    enabled: needsReadings,
    staleTime: Infinity,
  })
  if (detail.isLoading) return <div className="empty">Reading the case…</div>
  if (detail.error) {
    return (
      <div className="page">
        <ErrorEnvelope error={detail.error} />
        <Link to="/cases">Back to the queue</Link>
      </div>
    )
  }
  if (!detail.data) return <div className="empty">No such case.</div>

  const data = detail.data

  return (
    <div className="page case-page">
      <CaseHeader
        data={data}
        readings={readings.data ?? null}
        cgm={cgm}
        onAsk={() => setAsking(true)}
      />

      {asking ? <CaseChat caseId={caseId as string} onClose={() => setAsking(false)} /> : null}

      <NeedsALook data={data} readOnly={readOnly} />

      {/* The replies before the Tribunal or the High Court, where the file
          holds the papers of one. Above the card: a case opened with court
          papers is here for these. */}
      <CourtDocuments caseId={data.id} hasGist={Boolean(data.gist_en || data.gist_hi)} />

      <NextActionCard data={data} readOnly={readOnly} />

      {!readOnly ? (
        <div className="came-in-and-note">
          <SomethingCameIn data={data} />
          <NoteLine data={data} />
        </div>
      ) : null}

      <CaseFile data={data} readings={readings.data ?? null} readOnly={readOnly} />

      <DocumentsRail data={data} readOnly={readOnly} />

      {/* Outside the rail, because the rail is the twenty. These are what the
          office writes alongside them. */}
      <AdhocSections data={data} readOnly={readOnly} />

      {cgm ? (
        <p className="small muted" data-testid="cgm-footer">
          Read and ask only. Nothing on this page changes the case.
        </p>
      ) : null}
    </div>
  )
}

/**
 * Correspondence and anything else outside the twenty.
 *
 * Ruled 30 Aug 2026 (design doc point 6): officers write correspondence every
 * day and the product helped with none of it.
 *
 * **Below the rail, never on it.** The rail is the statutory path through a
 * proceeding, stage by stage, and it is what a reviewer reads to see whether
 * the proceeding was conducted properly. A covering letter has no place in
 * that sequence, and giving it one would misrepresent the case.
 *
 * A document whose template is unpublished is listed with the reason rather
 * than hidden — it is the officer's own half-finished work, and stop list 7 is
 * the same instinct.
 */
/**
 * Everything this office writes that is not one of the twenty — in **two
 * sections**, because they are two things.
 *
 * Archit, 5 Sep: *"we will separate the documents from correspondences as one
 * is docs and another is mail."* The schema half-knew already: `AdhocDocumentRow`
 * says in its own docstring that *"correspondence is not on the statutory path"*,
 * and then gave it no way to say which it is.
 *
 * **So the split is drawn and not yet decided.** There is no `kind` on the row,
 * so until one exists everything ad-hoc lands under Documents and
 * Correspondence stands empty with its own name. Sorting them on a guess would
 * be a client inventing a fact about a document, which is the one thing this
 * product does not do — and an empty section that is honest about being empty
 * is worth more than a full one that is wrong.
 *
 * The second half of that conversation — an officer describing a one-off letter
 * and the model writing it, with no template at all — is **written down and not
 * built**: it is a general-purpose generator inside a disciplinary proceeding,
 * which is ADR-021's territory, and it is on the Confluence page of things
 * agreed and not yet started.
 */
function AdhocSections({ data, readOnly }: { data: CaseDetail; readOnly: boolean }) {
  const rows = data.other_documents ?? []
  return (
    <>
      <AdhocSection
        id="other-documents"
        title="Other documents"
        rows={rows}
        data={data}
        readOnly={readOnly}
        empty={
          <>
            Nothing yet. A memo, a covering note — anything this office writes that is not one
            of the twenty is created in <Link to="/templates">Templates</Link> and appears here
            on every case.
          </>
        }
      />
      <AdhocSection
        id="correspondence"
        title="Correspondence"
        rows={[]}
        data={data}
        readOnly={readOnly}
        empty={
          <>
            Nothing yet. Letters and reminders that go out on this case will be listed here — a
            published template, or one written for this case alone.
          </>
        }
      />
    </>
  )
}

function AdhocSection({
  id,
  title,
  rows,
  data,
  readOnly,
  empty,
}: {
  id: string
  title: string
  rows: NonNullable<CaseDetail['other_documents']>
  data: CaseDetail
  readOnly: boolean
  empty: React.ReactNode
}) {
  /* **Closed unless there is something in it.** At pilot most cases have no
     ad-hoc documents at all, so two empty sections at the foot of every case
     page is two headings and eight lines of prose explaining twice that there
     is nothing there. Open when it holds something, closed when it does not,
     and either way one click the other way. The header carries the count, so
     a closed section still answers the only question a closed section is
     asked. */
  const [open, setOpen] = useState(rows.length > 0)
  return (
    <section className="adhoc" data-testid={id}>
      {/* The same caret and the same header-as-button as a stage of the
          journey — an officer learns one way of opening a thing on this page,
          not two. */}
      <button
        type="button"
        className="ah"
        aria-expanded={open}
        data-testid={`${id}-toggle`}
        onClick={() => setOpen((was) => !was)}
      >
        <span className="caret" aria-hidden="true">
          {open ? '\u25be' : '\u25b8'}
        </span>
        <span className="ti">{title}</span>
        <span className="cnt mono">
          {rows.length === 0 ? 'none yet' : `${rows.length} on this case`}
        </span>
      </button>
      {!open ? null : (
        <>
          {rows.length === 0 ? <p className="empty-note">{empty}</p> : null}
          {rows.map((row) => (
            <div className="r" key={row.document_type}>
              {/* English only, as everywhere in the chrome — the Hindi name belongs
              to the document, not to this screen. */}
              <span className="rt">
                {row.title}
                {row.occurrences > 0 ? (
                  <span className="small muted"> · {row.occurrences} on this case</span>
                ) : null}
                {row.not_generatable_reason ? (
                  <span className="why" data-testid={`no-generate-${row.document_type}`}>
                    {row.not_generatable_reason}{' '}
                    <Link to={`/templates/${row.document_type}`}>Open it</Link>
                  </span>
                ) : null}
              </span>
              <span className="act">
                {row.occurrences > 0 ? (
                  <Link className="btn" to={`/cases/${data.id}/documents/${row.document_type}`}>
                    View
                  </Link>
                ) : row.generatable && !readOnly ? (
                  <Link className="btn" to={`/cases/${data.id}/documents/${row.document_type}`}>
                    Generate
                  </Link>
                ) : null}
              </span>
            </div>
          ))}
        </>
      )}
    </section>
  )
}
